import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLanguages } from "@/hooks/useLanguages";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Search, BookOpen, Languages } from "lucide-react";

interface CustomTranslation {
  id: string;
  source_text: string;
  source_language: string;
  target_language: string;
  translated_text: string;
  context: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AdminTranslationDictionary = () => {
  const [translations, setTranslations] = useState<CustomTranslation[]>([]);
  const [filteredTranslations, setFilteredTranslations] = useState<CustomTranslation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState<CustomTranslation | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSourceLang, setSelectedSourceLang] = useState<string>("all");
  const [selectedTargetLang, setSelectedTargetLang] = useState<string>("all");
  const [formData, setFormData] = useState({
    source_text: "",
    source_language: "es",
    target_language: "en",
    translated_text: "",
    context: "",
    is_active: true,
  });

  const { languages } = useLanguages();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchTranslations();
  }, []);

  useEffect(() => {
    filterTranslations();
  }, [translations, searchQuery, selectedSourceLang, selectedTargetLang]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
      return;
    }

    const { data: adminUser } = await supabase
      .from('admin_users')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (!adminUser) {
      navigate('/admin/login');
    }
  };

  const fetchTranslations = async () => {
    try {
      const { data, error } = await supabase
        .from('custom_translations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching translations:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar las traducciones",
          variant: "destructive",
        });
        return;
      }

      setTranslations(data || []);
    } catch (error) {
      console.error('Error fetching translations:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las traducciones",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterTranslations = () => {
    let filtered = translations;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.source_text.toLowerCase().includes(query) ||
        t.translated_text.toLowerCase().includes(query) ||
        (t.context && t.context.toLowerCase().includes(query))
      );
    }

    if (selectedSourceLang !== "all") {
      filtered = filtered.filter(t => t.source_language === selectedSourceLang);
    }

    if (selectedTargetLang !== "all") {
      filtered = filtered.filter(t => t.target_language === selectedTargetLang);
    }

    setFilteredTranslations(filtered);
  };

  const resetForm = () => {
    setFormData({
      source_text: "",
      source_language: "es",
      target_language: "en",
      translated_text: "",
      context: "",
      is_active: true,
    });
    setEditingTranslation(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      let error;
      if (editingTranslation) {
        const { error: updateError } = await supabase
          .from('custom_translations')
          .update(formData)
          .eq('id', editingTranslation.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('custom_translations')
          .insert([formData]);
        error = insertError;
      }

      if (error) {
        console.error('Error saving translation:', error);
        toast({
          title: "Error",
          description: error.code === '23505' 
            ? "Ya existe una traducción para este texto en estos idiomas"
            : `No se pudo ${editingTranslation ? 'actualizar' : 'crear'} la traducción`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Éxito",
        description: `Traducción ${editingTranslation ? 'actualizada' : 'creada'} exitosamente`,
      });

      setIsDialogOpen(false);
      resetForm();
      fetchTranslations();
    } catch (error) {
      console.error('Error saving translation:', error);
      toast({
        title: "Error",
        description: `No se pudo ${editingTranslation ? 'actualizar' : 'crear'} la traducción`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (translation: CustomTranslation) => {
    setEditingTranslation(translation);
    setFormData({
      source_text: translation.source_text,
      source_language: translation.source_language,
      target_language: translation.target_language,
      translated_text: translation.translated_text,
      context: translation.context || "",
      is_active: translation.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Estás seguro de que quieres eliminar esta traducción?")) return;

    try {
      const { error } = await supabase
        .from('custom_translations')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting translation:', error);
        toast({
          title: "Error",
          description: "No se pudo eliminar la traducción",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Éxito",
        description: "Traducción eliminada exitosamente",
      });

      fetchTranslations();
    } catch (error) {
      console.error('Error deleting translation:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la traducción",
        variant: "destructive",
      });
    }
  };

  const getLanguageName = (code: string) => {
    const lang = languages.find(l => l.code === code);
    return lang ? `${lang.flag_emoji} ${lang.native_name}` : code.toUpperCase();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Diccionario de Traducciones</h1>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva Traducción
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingTranslation ? "Editar Traducción" : "Nueva Traducción"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="source_language">Idioma Origen</Label>
                    <Select
                      value={formData.source_language}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, source_language: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map((language) => (
                          <SelectItem key={language.code} value={language.code}>
                            {getLanguageName(language.code)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="target_language">Idioma Destino</Label>
                    <Select
                      value={formData.target_language}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, target_language: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map((language) => (
                          <SelectItem key={language.code} value={language.code}>
                            {getLanguageName(language.code)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source_text">Texto Original *</Label>
                  <Textarea
                    id="source_text"
                    value={formData.source_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, source_text: e.target.value }))}
                    placeholder="Ingresa el texto original"
                    required
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="translated_text">Texto Traducido *</Label>
                  <Textarea
                    id="translated_text"
                    value={formData.translated_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, translated_text: e.target.value }))}
                    placeholder="Ingresa la traducción"
                    required
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="context">Contexto (opcional)</Label>
                  <Input
                    id="context"
                    value={formData.context}
                    onChange={(e) => setFormData(prev => ({ ...prev, context: e.target.value }))}
                    placeholder="Contexto donde se usa esta traducción"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="is_active">Activo</Label>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? "Guardando..." : editingTranslation ? "Actualizar" : "Crear"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtros de Búsqueda</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Buscar texto..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Idioma Origen</Label>
                <Select value={selectedSourceLang} onValueChange={setSelectedSourceLang}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los idiomas</SelectItem>
                    {languages.map((language) => (
                      <SelectItem key={language.code} value={language.code}>
                        {getLanguageName(language.code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Idioma Destino</Label>
                <Select value={selectedTargetLang} onValueChange={setSelectedTargetLang}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los idiomas</SelectItem>
                    {languages.map((language) => (
                      <SelectItem key={language.code} value={language.code}>
                        {getLanguageName(language.code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Total</Label>
                <div className="flex items-center h-10 px-3 border rounded-md bg-muted/50">
                  <span className="text-sm text-muted-foreground">
                    {filteredTranslations.length} traducciones
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Translations List */}
        {filteredTranslations.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BookOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {translations.length === 0 
                  ? "No hay traducciones personalizadas" 
                  : "No se encontraron traducciones"
                }
              </h3>
              <p className="text-muted-foreground text-center mb-6">
                {translations.length === 0 
                  ? "Crea traducciones personalizadas para tener control total sobre el contenido traducido."
                  : "Ajusta los filtros de búsqueda para encontrar las traducciones que necesitas."
                }
              </p>
              {translations.length === 0 && (
                <Button onClick={() => {resetForm(); setIsDialogOpen(true)}}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Primera Traducción
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredTranslations.map((translation) => (
              <Card key={translation.id} className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {getLanguageName(translation.source_language)}
                        </Badge>
                        <span className="text-sm text-muted-foreground">Texto Original</span>
                      </div>
                      <p className="text-sm bg-muted/50 p-3 rounded-md">
                        {translation.source_text}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {getLanguageName(translation.target_language)}
                        </Badge>
                        <span className="text-sm text-muted-foreground">Traducción</span>
                      </div>
                      <p className="text-sm bg-muted/50 p-3 rounded-md">
                        {translation.translated_text}
                      </p>
                    </div>
                  </div>
                  
                  {translation.context && (
                    <div className="mt-4">
                      <span className="text-sm text-muted-foreground">Contexto: </span>
                      <span className="text-sm italic">{translation.context}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">
                      <Badge variant={translation.is_active ? "outline" : "secondary"}>
                        {translation.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(translation.created_at).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(translation)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(translation.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTranslationDictionary;